import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import catalogFallback from '../data/catalog-data.json';
import { GolfFlagIcon, Ps2ControllerIcon } from './Icons';
import { usePS2Graphics } from '../context/PS2GraphicsContext';

/**
 * Premium Hero — mouse-reactive parallax, staggered entrance animations,
 * and a magnetic hover effect on the logo.
 * When PS2 mode is active, swaps to blue/cyan palette and uses the PS2 boot-style logo.
 */
export default function Hero() {
  const { ps2Mode } = usePS2Graphics();
  const [status, setStatus] = useState({ activeGames: 0, playersOnline: 0, serverOnline: false });
  const [loading, setLoading] = useState(true);
  const [gameCount, setGameCount] = useState(catalogFallback.length);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const socket = io();
    socket.on('status_updated', (data) => { setStatus(data); setLoading(false); });
    socket.on('games_updated', () => { setLoading(false); });
    return () => socket.disconnect();
  }, []);

  // Fetch catalog count
  useEffect(() => {
    fetch('/api/catalog/meta')
      .then(r => r.json())
      .then(data => setGameCount(data.count || catalogFallback.length))
      .catch(() => setGameCount(catalogFallback.length));
  }, []);

  // Mouse parallax
  const handleMouseMove = useCallback((e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2; // -1 to 1
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setMouse({ x, y });
  }, []);

  // Color palette based on mode
  const accent = ps2Mode ? '6,182,212' : '34,197,94'; // cyan vs emerald
  const accentHex = ps2Mode ? '#06b6d4' : '#22c55e';
  const accentClass = ps2Mode ? 'text-cyan-400' : 'text-emerald-400';
  const accentBgClass = ps2Mode ? 'bg-cyan-400' : 'bg-emerald-400';
  const accentBorderClass = ps2Mode ? 'border-cyan-400/10' : 'border-emerald-400/10';

  return (
    <section
      ref={heroRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setMouse({ x: 0, y: 0 })}
      className="relative flex items-center justify-center overflow-hidden py-20 md:py-28 lg:py-32"
    >
      {/* Hero ambient glow — follows mouse subtly */}
      <div
        className="absolute inset-0 transition-all duration-[2000ms] ease-out"
        style={{
          background: `radial-gradient(ellipse at ${50 + mouse.x * 10}% ${40 + mouse.y * 10}%, rgba(${accent},0.07), transparent 60%)`,
        }}
      />
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{
        background: `linear-gradient(to right, transparent, rgba(${accent},0.25), transparent)`,
      }} />

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full">
        {/* Logo — parallax layer 1 (most movement) */}
        <div
          className="flex justify-center mb-7 animate-boot-1"
          style={{
            transform: `translate(${mouse.x * 12}px, ${mouse.y * 8}px)`,
            transition: 'transform 0.6s cubic-bezier(0.2, 0, 0, 1)',
          }}
        >
          {ps2Mode ? (
            /* PS2 Mode: Vertical bars logo from boot animation */
            <div className="relative group cursor-default">
              {/* Outer glow */}
              <div className="absolute -inset-8 rounded-full opacity-50 group-hover:opacity-80 transition-opacity duration-1000"
                style={{
                  background: 'radial-gradient(circle, rgba(6,182,212,0.2), transparent 70%)',
                  filter: 'blur(25px)',
                }}
              />
              {/* Vertical bars */}
              <div className="relative flex items-end gap-[4px]">
                {[22, 34, 48, 56, 48, 34, 22].map((h, i) => (
                  <div
                    key={i}
                    className="rounded-sm"
                    style={{
                      width: '7px',
                      height: `${h}px`,
                      background: 'linear-gradient(to top, rgba(6,182,212,0.2), rgba(6,182,212,0.7), rgba(147,197,253,0.9))',
                      boxShadow: '0 0 10px rgba(6,182,212,0.4), 0 0 20px rgba(6,182,212,0.15)',
                      animation: `boot-bar-rise 1.2s ease-out ${i * 0.06}s both`,
                    }}
                  />
                ))}
              </div>
              {/* Pulse rings */}
              <div className="absolute -inset-4 rounded-full border border-cyan-400/10 animate-ping" style={{ animationDuration: '4s' }} />
            </div>
          ) : (
            /* Default Mode: Golf circle logo */
            <div className="relative group cursor-default">
              {/* Outer glow ring */}
              <div className="absolute -inset-5 rounded-full opacity-40 group-hover:opacity-80 transition-opacity duration-1000"
                style={{
                  background: 'radial-gradient(circle, rgba(34,197,94,0.15), transparent 70%)',
                  filter: 'blur(20px)',
                }}
              />
              {/* Main circle */}
              <div className="relative w-22 h-22 md:w-28 md:h-28 rounded-full flex items-center justify-center group-hover:scale-105 transition-transform duration-700"
                style={{
                  width: '6.5rem', height: '6.5rem',
                  background: 'linear-gradient(145deg, rgba(34,197,94,0.1), rgba(34,197,94,0.03))',
                  border: '1.5px solid rgba(34,197,94,0.2)',
                  boxShadow: '0 0 50px rgba(34,197,94,0.08), inset 0 1px 2px rgba(255,255,255,0.05), 0 20px 60px rgba(0,0,0,0.3)',
                }}
              >
                <GolfFlagIcon className="w-12 h-12 text-emerald-400" style={{ filter: 'drop-shadow(0 4px 12px rgba(34,197,94,0.3))' }} />
              </div>
              {/* Pulse rings */}
              <div className="absolute -inset-2 rounded-full border border-emerald-400/10 animate-ping" style={{ animationDuration: '4s' }} />
              <div className="absolute -inset-4 rounded-full border border-emerald-400/5 animate-ping" style={{ animationDuration: '6s', animationDelay: '1s' }} />
            </div>
          )}
        </div>

        {/* Title — parallax layer 2 */}
        <div
          style={{
            transform: `translate(${mouse.x * 6}px, ${mouse.y * 4}px)`,
            transition: 'transform 0.8s cubic-bezier(0.2, 0, 0, 1)',
          }}
        >
          {ps2Mode ? (
            /* PS2 Mode: Gradient text title like boot animation */
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-3 animate-boot-1"
              style={{
                background: 'linear-gradient(180deg, #93c5fd, #06b6d4, #3b82f6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 4px 30px rgba(6,182,212,0.3))',
              }}
            >
              The CountryClub
            </h1>
          ) : (
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tighter mb-3 animate-boot-1"
              style={{ textShadow: '0 4px 60px rgba(34,197,94,0.12), 0 2px 20px rgba(0,0,0,0.5)' }}
            >
              The Country<span className="text-emerald-400" style={{ textShadow: '0 0 40px rgba(34,197,94,0.35), 0 0 80px rgba(34,197,94,0.15)' }}>Club</span>
            </h1>
          )}
        </div>

        {/* Tagline — parallax layer 3 */}
        <div
          className="animate-boot-2"
          style={{
            transform: `translate(${mouse.x * 3}px, ${mouse.y * 2}px)`,
            transition: 'transform 1s cubic-bezier(0.2, 0, 0, 1)',
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-10" style={{ background: `linear-gradient(to right, transparent, rgba(${accent},0.2))` }} />
            <p className="text-[11px] md:text-xs text-white/35 font-mono tracking-[0.25em] uppercase">
              {ps2Mode ? 'PS2 Online Revival · 128-Bit · Members Only' : 'est. 2026 · members only · dress code: pajamas'}
            </p>
            <div className="h-px w-10" style={{ background: `linear-gradient(to left, transparent, rgba(${accent},0.2))` }} />
          </div>

          <p className="text-sm md:text-base lg:text-lg text-white/55 max-w-xl mx-auto leading-relaxed font-light mb-8">
            A revival community bringing classic online multiplayer back to life.{' '}
            <br className="hidden md:block" />
            Browse active games, find lobbies, and connect with players.
          </p>
        </div>

        {/* Live stat chips — glass pills */}
        <div className="inline-flex flex-wrap items-center justify-center gap-2.5 md:gap-3 animate-boot-3">
          {/* Online status */}
          <div className="group inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full text-xs font-mono transition-all duration-500 hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 1px rgba(255,255,255,0.04)',
            }}
          >
            <span className="relative flex h-2 w-2">
              <span className={`absolute inset-0 rounded-full ${status.serverOnline ? accentBgClass + ' animate-ping' : 'bg-white/20'}`} style={{ animationDuration: '2s' }} />
              <span className={`relative rounded-full h-2 w-2 ${status.serverOnline ? accentBgClass : 'bg-white/20'}`} style={{ boxShadow: status.serverOnline ? `0 0 8px rgba(${accent},0.5)` : 'none' }} />
            </span>
            <span className="text-white/60 group-hover:text-white/80 transition-colors">
              {loading ? '...' : status.playersOnline > 0 ? <><span className={`${accentClass} font-bold`}>{status.playersOnline}</span> at the Clubhouse</> : 'Clubhouse Open'}
            </span>
          </div>

          {/* Game count */}
          <div className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-mono text-white/50 hover:text-white/70 transition-all duration-500 hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            }}
          >
            <Ps2ControllerIcon className="w-4 h-4 text-cyan-400" />
            <span><span className="text-white/70 font-semibold">{gameCount || '...'}</span> Games on the Menu</span>
          </div>

          {/* Get Started CTA */}
          <Link
            to="/join"
            className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-mono transition-all duration-300 hover:scale-[1.03]"
            style={{
              background: `linear-gradient(135deg, rgba(${accent},0.12), rgba(${accent},0.04))`,
              border: `1px solid rgba(${accent},0.2)`,
              boxShadow: `0 2px 12px rgba(${accent},0.08)`,
            }}
          >
            <GolfFlagIcon className={`w-4 h-4 ${accentClass}`} />
            <span className={`${ps2Mode ? 'text-cyan-400/70 group-hover:text-cyan-400' : 'text-emerald-400/70 group-hover:text-emerald-400'} transition-colors font-semibold`}>Get Started</span>
          </Link>
        </div>

        {/* Palworld Promo Card */}
        <div className="mt-10 md:mt-12 animate-boot-3">
          <div className="max-w-2xl mx-auto rounded-xl bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 p-5 md:p-6 shadow-[0_0_30px_rgba(249,115,22,0.1)] backdrop-blur-md transition-all hover:border-orange-500/50 hover:shadow-[0_0_40px_rgba(249,115,22,0.15)] group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out" />
            <div className="flex flex-col md:flex-row items-center gap-5 relative z-10">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 border border-orange-500/40 flex items-center justify-center flex-shrink-0 hidden md:flex shadow-[0_0_15px_rgba(249,115,22,0.3)]">
                <span className="text-2xl">🌍</span>
              </div>
              <div className="flex-1 text-center md:text-left">
                <span className="text-[10px] font-mono font-extrabold uppercase tracking-[0.2em] text-orange-400 mb-1 block">New Server Online</span>
                <h3 className="text-lg md:text-xl font-bold text-white mb-1.5">Palworld: The Country Club</h3>
                <p className="text-xs text-white/60 font-mono leading-relaxed mb-3">
                  Join our new 24/7 dedicated Palworld community server! PC & Xbox cross-play supported.
                </p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                  <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Connect IP:</span>
                  <code className="px-2.5 py-1 bg-black/60 border border-white/10 rounded-md font-mono text-xs text-orange-300 font-bold select-all">
                    5.78.118.180:8211
                  </code>
                </div>
              </div>
              <div className="flex-shrink-0 mt-2 md:mt-0">
                <a href="https://socom.gg/palworld" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white font-bold text-xs rounded-lg transition-all shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 hover:-translate-y-0.5">
                  Live Dashboard →
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll CTA */}
        <div className="mt-10 flex justify-center animate-boot-4">
          <a href="#active-games" className={`group flex flex-col items-center gap-2 text-white/25 ${ps2Mode ? 'hover:text-cyan-400/60' : 'hover:text-emerald-400/60'} transition-all duration-500`}>
            <span className="text-[9px] font-mono tracking-[0.3em] uppercase group-hover:tracking-[0.4em] transition-all duration-500">Tee Off</span>
            <div className="relative">
              <svg className="w-5 h-5 animate-bounce" style={{ animationDuration: '2s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
